/**
 * Authentication Context
 *
 * Purpose: Provides authentication state management and user session handling
 *          for the OrchestrateHQ AI frontend application.
 *
 * Architecture: React Context with useReducer for state management, handles
 *              login, logout, token management, and user session persistence.
 *
 * Dependencies: React, API Service, Local Storage
 * Author: Development Team
 * Created: 2025-01-27
 * Last Updated: 2025-08-06
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { AuthState, User, LoginRequest, Tenant, ModuleLevel } from '../types/auth';
import { ModuleKey, PermissionKey } from '../constants/permissionKeys';
import { activeTenantFeatures, userCan, userHasModule } from '../utils/moduleAccess';
import { FeatureToggle } from '../types/tenant';
import { apiService } from '../services/api';

/**
 * Authentication action types for state management
 */
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'AUTH_CHECK_START' }
  | { type: 'AUTH_CHECK_COMPLETE' }
  | { type: 'CLEAR_ERROR' };

/**
 * Initial authentication state
 */
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null, // Don't read from localStorage at module load time
  loading: false, // Don't set loading to true on initial load
  error: null,
};

/**
 * Authentication state reducer
 *
 * Purpose: Manages authentication state transitions
 * Logic: Handles different authentication actions and updates state accordingly
 * Input: state - Current authentication state
 *        action - Action to perform on the state
 * Output: New authentication state
 *
 * @param state - Current authentication state
 * @param action - Action to perform
 * @returns New authentication state
 */
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false,
      };
    case 'AUTH_CHECK_START':
      return {
        ...state,
        loading: true,
      };
    case 'AUTH_CHECK_COMPLETE':
      return {
        ...state,
        loading: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

/**
 * Authentication context interface
 */
interface AuthContextType {
  /** Current authentication state */
  state: AuthState;
  /** Login function for email/password authentication */
  login: (credentials: LoginRequest) => Promise<void>;
  /** Login function for Google OAuth authentication */
  googleLogin: (credential: string) => Promise<void>;
  /** Select tenant for current session */
  selectTenant: (tenant_id: string) => Promise<void>;
  /** Logout function to clear session */
  logout: () => Promise<void>;
  /** Clear error messages */
  clearError: () => void;
  /** Re-fetch the signed-in user (e.g. after a profile save changed their locale). */
  refreshUser: () => Promise<void>;
  /** Check if user has specific feature enabled */
  hasFeature: (feature: FeatureToggle) => boolean;
  /** Get current tenant information */
  getCurrentTenant: () => Tenant | null;
  /** Whether the user holds a permission in the active tenant */
  can: (key: PermissionKey) => boolean;
  /** Whether the user holds any of the permissions in the active tenant */
  canAny: (keys: PermissionKey[]) => boolean;
  /** Whether the user holds an area (and its parent) at the level, in the active tenant */
  hasModule: (key: ModuleKey, level?: ModuleLevel) => boolean;
}

/**
 * Authentication context instance
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props interface for AuthProvider component
 */
interface AuthProviderProps {
  /** Child components to wrap with authentication context */
  children: ReactNode;
  /** TEST ONLY: Skip the auth check on mount (for testing purposes) */
  _testSkipAuthCheck?: boolean;
  /** TEST ONLY: Force re-run auth check even if already checked (for testing purposes) */
  _testForceAuthCheck?: boolean;
}

/**
 * Extracts error message from various error response formats
 *
 * Purpose: Provides consistent error message extraction from API responses
 * Logic: Handles different error response structures and extracts meaningful messages
 * Input: error - Error object from API call
 * Output: Formatted error message string
 *
 * @param error - Error object from API call
 * @returns Formatted error message
 */
const extractErrorMessage = (error: any): string => {
  let errorMessage = 'An unexpected error occurred. Please try again.';

  if (error.response?.data) {
    const data = error.response.data;
    if (typeof data === 'string') {
      errorMessage = data;
    } else if (data.message) {
      errorMessage = data.message;
    } else if (data.detail?.message) {
      errorMessage = data.detail.message;
    } else if (data.detail && typeof data.detail === 'string') {
      errorMessage = data.detail;
    }
  } else if (error.message) {
    errorMessage = error.message;
  }

  return errorMessage;
};

/**
 * Stores authentication tokens securely
 *
 * Purpose: Centralizes token storage logic for consistency and security
 * Logic: Stores tokens in localStorage with consistent naming
 * Input: token - Access token
 *        refreshToken - Refresh token
 * Output: None (side effect: stores tokens)
 *
 * @param token - Access token to store
 * @param refreshToken - Refresh token to store
 */
const storeTokens = (token: string, refreshToken: string): void => {
  localStorage.setItem('orchestratehq_token', token);
  localStorage.setItem('orchestratehq_refresh_token', refreshToken);
};

/**
 * Stores user data securely
 *
 * Purpose: Centralizes user data storage for consistency and access
 * Logic: Stores user data in localStorage with consistent naming
 * Input: user - User object to store
 * Output: None (side effect: stores user data)
 *
 * @param user - User object to store
 */
const storeUser = (user: User): void => {
  localStorage.setItem('orchestratehq_user', JSON.stringify(user));
};

/**
 * Clears all authentication tokens and user data
 *
 * Purpose: Centralizes token and user data cleanup logic for consistency and security
 * Logic: Removes all authentication tokens and user data from localStorage
 * Input: None
 * Output: None (side effect: removes tokens and user data)
 */
const clearTokens = (): void => {
  localStorage.removeItem('orchestratehq_token');
  localStorage.removeItem('orchestratehq_refresh_token');
  localStorage.removeItem('orchestratehq_user');
};

/**
 * Authentication provider component
 *
 * Purpose: Provides authentication context to the application
 * Logic: Manages authentication state, handles login/logout, and persists user sessions
 * Input: children - React components to wrap with authentication context
 * Output: AuthContext.Provider with authentication state and methods
 *
 * @param children - Child components to wrap with authentication context
 * @returns AuthContext.Provider component
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  _testSkipAuthCheck = false,
  _testForceAuthCheck = false,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const hasCheckedAuthRef = useRef(false);

  /**
   * Checks if user is already authenticated on app load
   *
   * Purpose: Restores user session from stored tokens
   * Logic: Validates stored token and fetches current user data
   * Input: None
   * Output: None (side effect: updates authentication state)
   */
  useEffect(() => {
    // TEST ONLY: Allow skipping auth check entirely
    if (_testSkipAuthCheck) {
      return;
    }

    // Prevent multiple auth checks (guard is reset in cleanup for React StrictMode)
    if (hasCheckedAuthRef.current && !_testForceAuthCheck) {
      return;
    }

    hasCheckedAuthRef.current = true;

    // Set up callback for token refresh failures FIRST
    let callbackCalled = false;
    const handleTokenRefreshFailure = () => {
      if (callbackCalled) {
        return;
      }
      callbackCalled = true;
      clearTokens();
      dispatch({ type: 'LOGOUT' }); // Use LOGOUT to clear all state including token

      // If we're not already on the login page, redirect there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    // Register the callback with the API service
    apiService.setTokenRefreshFailureCallback(handleTokenRefreshFailure);

    const checkAuthStatus = async () => {
      const token = localStorage.getItem('orchestratehq_token');

      // If we have a token, check if it's expired before even trying to use it
      if (token) {
        try {
          // Try to decode the token to check if it's expired
          const payload = JSON.parse(atob(token.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          if (payload.exp && payload.exp < now) {
            clearTokens();
            dispatch({ type: 'AUTH_CHECK_COMPLETE' });
            return;
          }
        } catch (error) {
          clearTokens();
          dispatch({ type: 'AUTH_CHECK_COMPLETE' });
          return;
        }

        dispatch({ type: 'AUTH_CHECK_START' });

        // Set a timeout to ensure loading state is reset even if something goes wrong
        const timeoutId = setTimeout(() => {
          if (!callbackCalled) {
            dispatch({ type: 'AUTH_CHECK_COMPLETE' });
          }
        }, 3000); // 3 second timeout

        try {
          const response = await apiService.getCurrentUser();
          clearTimeout(timeoutId); // Clear timeout on success

          if (response.status === 'success' && response.data) {
            storeUser(response.data);
            dispatch({ type: 'SET_USER', payload: response.data });
          } else {
            // Invalid token, clear it
            clearTokens();
            dispatch({ type: 'LOGOUT' }); // Use LOGOUT to clear all state
          }
        } catch (error) {
          clearTimeout(timeoutId); // Clear timeout on error
          // Clear tokens on any error to prevent infinite loops
          clearTokens();
          dispatch({ type: 'LOGOUT' }); // Use LOGOUT to clear all state

          // If we're not already on the login page, redirect there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } else {
        // Ensure loading state is false when no token
        dispatch({ type: 'AUTH_CHECK_COMPLETE' });
      }
    };

    checkAuthStatus();

    // Cleanup function to remove callback on unmount
    return () => {
      apiService.setTokenRefreshFailureCallback(() => {});
      // Reset guard for React StrictMode (allows effect to re-run on remount)
      hasCheckedAuthRef.current = false;
    };
  }, [_testSkipAuthCheck, _testForceAuthCheck]); // Add test props to dependencies

  /**
   * Handles email/password login
   *
   * Purpose: Authenticates user with email and password credentials
   * Logic: Calls API service, stores tokens, and updates authentication state
   * Input: credentials - Login credentials (email and password)
   * Output: Promise that resolves on success or rejects on failure
   *
   * @param credentials - Login credentials
   * @returns Promise that resolves on successful login
   * @throws Error on login failure
   */
  const login = async (credentials: LoginRequest): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await apiService.login(credentials);

      if (response.status === 'success') {
        const { token, refreshToken, user } = response.data;

        // Store tokens and user data securely
        storeTokens(token, refreshToken);
        storeUser(user);

        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      } else {
        throw new Error((response as any).message || 'Login failed');
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Handles Google OAuth login
   *
   * Purpose: Authenticates user with Google OAuth credentials
   * Logic: Calls API service with Google credential, stores tokens, and updates state
   * Input: credential - Google OAuth credential token
   * Output: Promise that resolves on success or rejects on failure
   *
   * @param credential - Google OAuth credential token
   * @returns Promise that resolves on successful login
   * @throws Error on login failure
   */
  const googleLogin = async (credential: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await apiService.googleLogin(credential);

      if (response.status === 'success') {
        const { token, refreshToken, user } = response.data;

        // Store tokens and user data securely
        storeTokens(token, refreshToken);
        storeUser(user);

        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      } else {
        throw new Error((response as any).message || 'Google login failed');
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Handles tenant selection for current session
   *
   * Purpose: Selects active tenant and updates session with tenant context
   * Logic: Calls API service to select tenant, stores new tokens, and updates state
   * Input: tenant_id - Tenant ID to select
   * Output: Promise that resolves on success or rejects on failure
   *
   * @param tenant_id - Tenant ID to select
   * @returns Promise that resolves on successful tenant selection
   * @throws Error on tenant selection failure
   */
  const selectTenant = async (tenant_id: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await apiService.selectTenant(tenant_id);

      if (response.status === 'success') {
        const { token, refreshToken, user } = response.data;

        // Store new tokens and user data securely
        storeTokens(token, refreshToken);
        storeUser(user);

        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      } else {
        throw new Error((response as any).message || 'Tenant selection failed');
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Handles user logout
   *
   * Purpose: Clears user session and authentication state
   * Logic: Calls logout API, clears local tokens, and resets authentication state
   * Input: None
   * Output: Promise that resolves when logout is complete
   *
   * @returns Promise that resolves when logout is complete
   */
  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state regardless of API call success
      clearTokens();
      dispatch({ type: 'LOGOUT' });
    }
  };

  /**
   * Clears authentication error messages
   *
   * Purpose: Allows users to dismiss error messages
   * Logic: Dispatches CLEAR_ERROR action to reset error state
   * Input: None
   * Output: None (side effect: clears error state)
   */
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  /**
   * Checks if user has specific feature enabled
   *
   * Purpose: Feature-based authorization for UI elements
   * Logic: Checks the active tenant's enabled features (see activeTenantFeatures)
   * Input: feature - Feature toggle to check
   * Output: Boolean indicating if user has access to feature
   *
   * @param feature - Feature toggle to check
   * @returns Boolean indicating feature access
   */
  const hasFeature = (feature: FeatureToggle): boolean =>
    activeTenantFeatures(state.user).includes(feature);

  /**
   * Gets current tenant information
   *
   * Purpose: Provides tenant context for UI components
   * Logic: Returns active tenant object from current user, with fallback to first available
   * Input: None
   * Output: Tenant object or null
   *
   * @returns Current tenant information or null
   */
  const getCurrentTenant = (): Tenant | null => {
    // First, try to get the explicitly set active tenant
    if (state.user?.activeTenant) {
      return {
        tenant_id: state.user.activeTenant.tenant_id,
        name: state.user.activeTenant.name,
        base_currency: 'USD', // Default values for now
        base_language: 'en',
        status: 'active',
      };
    }

    // Fallback: if user has available tenants but no active tenant set,
    // return the first available tenant as a default
    if (state.user?.availableTenants && state.user.availableTenants.length > 0) {
      const firstTenant = state.user.availableTenants[0];
      return {
        tenant_id: firstTenant.tenant_id,
        name: firstTenant.name,
        base_currency: 'USD',
        base_language: 'en',
        status: firstTenant.status || 'active',
      };
    }

    return null;
  };

  // Stable per signed-in user, so hooks can list them as dependencies
  const can = useCallback((key: PermissionKey): boolean => userCan(state.user, key), [state.user]);

  const canAny = useCallback(
    (keys: PermissionKey[]): boolean => keys.some((key) => userCan(state.user, key)),
    [state.user]
  );

  const hasModule = useCallback(
    (key: ModuleKey, level: ModuleLevel = 'read'): boolean => userHasModule(state.user, key, level),
    [state.user]
  );

  // Memoize the context value to ensure it updates when state changes
  // This helps React detect state changes and trigger re-renders in consuming components
  /**
   * Re-fetch the current user from the server
   *
   * Purpose: A profile save can change the person's formatting locale or clock
   *          (ORCH-191); the auth payload is where the frontend reads them from.
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const response = await apiService.getCurrentUser();
      if (response.status === 'success' && response.data) {
        storeUser(response.data);
        dispatch({ type: 'SET_USER', payload: response.data });
      }
    } catch (error) {
      console.error('Failed to refresh the current user', error);
    }
  }, []);

  const value: AuthContextType = useMemo(
    () => ({
      state,
      login,
      googleLogin,
      selectTenant,
      logout,
      clearError,
      refreshUser,
      hasFeature,
      getCurrentTenant,
      can,
      canAny,
      hasModule,
    }),
    [
      state,
      login,
      googleLogin,
      selectTenant,
      logout,
      clearError,
      refreshUser,
      hasFeature,
      getCurrentTenant,
      can,
      canAny,
      hasModule,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use authentication context
 *
 * Purpose: Provides easy access to authentication state and methods
 * Logic: Returns authentication context or throws error if used outside provider
 * Input: None
 * Output: Authentication context with state and methods
 *
 * @returns Authentication context
 * @throws Error if used outside AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
